import { useState, useEffect, useRef } from 'react';
import { getServices, getServiceCategories } from '../services/services';

function Services({ 
  mode = 'display', // 'display' or 'booking'
  onServiceSelect = null, // callback for booking mode
  onServiceClick = null, // callback for display mode service clicks
  services: propServices = null, // services passed from parent
  categories: propCategories = null, // categories passed from parent
  selectedService = null, // selected service for booking mode
  businessId = null // required for API calls when services/categories not provided
}) {
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [filteredServices, setFilteredServices] = useState([]);
  const [expandedServices, setExpandedServices] = useState({});
  const [showAllServices, setShowAllServices] = useState(false);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    // Use prop data if provided, otherwise fetch from API
    if (propServices && propCategories) {
      setServices(Array.isArray(propServices) ? propServices : []);
      setCategories(Array.isArray(propCategories) ? propCategories : []);
      setFilteredServices(Array.isArray(propServices) ? propServices : []);
    } else if (businessId) {
      const fetchData = async () => {
        try {
          const servicesData = await getServices(businessId);
          const categoriesData = await getServiceCategories(businessId);
          console.log('Fetched services data:', servicesData);
          console.log('Fetched categories data:', categoriesData);
          
          setServices(Array.isArray(servicesData) ? servicesData : []);
          setCategories(Array.isArray(categoriesData) ? categoriesData : []);
          setFilteredServices(Array.isArray(servicesData) ? servicesData : []);
        } catch (error) {
          console.error('Error fetching services data:', error);
          setServices([]);
          setCategories([]);
          setFilteredServices([]);
        }
      };
      fetchData();
    } else {
      console.error('Services component requires either services/categories props or businessId for API calls');
      setServices([]);
      setCategories([]);
      setFilteredServices([]);
    }
  }, [propServices, propCategories, businessId]);

  useEffect(() => {
    const servicesToFilter = propServices || services;
    if (selectedCategory === 'all') {
      setFilteredServices(servicesToFilter);
    } else {
      const filtered = servicesToFilter.filter(service => 
        service.category_id === selectedCategory || 
        service.category === selectedCategory ||
        service.category_name === selectedCategory
      );
      setFilteredServices(filtered);
    }
  }, [selectedCategory, services, propServices]);

  const toggleExpanded = (index) => {
    setExpandedServices(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
    setExpandedServices({});
    setShowAllServices(false); // Reset show more when changing categories
  };

  const handleServiceClick = (service) => {
    if (mode === 'booking' && onServiceSelect) {
      onServiceSelect(service);
    }
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  const categoriesToUse = propCategories || categories;

  if (!Array.isArray(categoriesToUse) || categoriesToUse.length === 0) {
    return <div>Loading...</div>;
  }

  // Calculate services to display based on show more functionality
  const SERVICES_PER_ROW = 2; // Assuming 2 services per row based on grid layout
  const INITIAL_ROWS = 4;
  const INITIAL_SERVICES_COUNT = SERVICES_PER_ROW * INITIAL_ROWS;
  
  const servicesToDisplay = showAllServices ? filteredServices : filteredServices.slice(0, INITIAL_SERVICES_COUNT);
  const hasMoreServices = filteredServices.length > INITIAL_SERVICES_COUNT;

    const renderServiceItem = (service, index) => {
      const isExpanded = expandedServices[index];
      const description = service.description || '';
      const shouldShowButton = description.length > 100 && mode === 'display';

      const isSelected = mode === 'booking' && selectedService?.id === service.id;
      const serviceItemClass = mode === 'booking' 
        ? `service-item booking-service-item ${isSelected ? 'selected' : ''}` 
        : 'service-item clickable-service';

      const handleClick = () => {
        if (mode === 'booking') {
          handleServiceClick(service);
        } else {
          // Open detailed service modal in display mode
          if (onServiceClick) {
            onServiceClick(service);
          }
        }
      };

      return (
        <div 
          key={service.id} 
          className={serviceItemClass}
          onClick={handleClick}
          style={{ cursor: 'pointer' }}
        >
          <div className="service-content">
            <h4 className="service-name">{service.service || service.service_name}</h4>
            <div className="service-description">
              <p>
                {mode === 'booking' || isExpanded ? description : truncateText(description)}
              </p>
              {shouldShowButton && (
                <button 
                  className="service-expand-button"
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent service selection in booking mode
                    toggleExpanded(index);
                  }}
                >
                  {isExpanded ? 'Show less' : 'Read more'}
                </button>
              )}
            </div>
            <div className="service-details">
              <span className="service-duration">{service.duration} min</span>
              <span className="service-price">{service.min_price ? `From £${service.price}` : service.price === 0 ? 'Free' : `£${service.price}`}</span>
            </div>
          </div>
        </div>
      );
  };

  return (
    <section id="services" className={`services ${mode === 'booking' ? 'services-booking' : ''}`}>
      <div className="container" data-aos="fade-up">
        
        {/* Category Filter Bar */}
        <div className="category-filter-container">
          <button className="scroll-button left" onClick={scrollLeft}>
            &#8249;
          </button>
          <div className="category-scroll-container" ref={scrollContainerRef}>
            <div className="category-filters">
              <button
                className={`category-filter-btn ${selectedCategory === 'all' ? 'active' : ''}`}
                onClick={() => handleCategorySelect('all')}
              >
                All
              </button>
              {categoriesToUse.map((category) => (
                <button
                  key={category.id}
                  className={`category-filter-btn ${selectedCategory === category.id ? 'active' : ''}`}
                  onClick={() => handleCategorySelect(category.id)}
                >
                  {category.category_name || category.name}
                </button>
              ))}
            </div>
          </div>
          <button className="scroll-button right" onClick={scrollRight}>
            &#8250;
          </button>
        </div>

        {/* Services Display */}
        <div className="section-content">
          <div className="services-grid">
            {servicesToDisplay.length > 0 ? (
              servicesToDisplay.map((service, index) => renderServiceItem(service, index))
            ) : (
              <div className="no-services">
                <p>No services found for this category.</p>
              </div>
            )}
          </div>
          
          {/* Show More/Less Button */}
          {hasMoreServices && (
            <div className="show-more-container">
              <button 
                className="show-more-btn"
                onClick={() => setShowAllServices(!showAllServices)}
              >
                {showAllServices ? 'Show Less' : `Show More`}
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

export default Services